import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Star, Quote, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const testimonials = [
  {
    name: 'Sarah Jenkins', role: 'CEO, TechVentures Inc.',
    avatar: 'https://images.pexels.com/photos/33871730/pexels-photo-33871730.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=100&w=100',
    quote: 'Softlume transformed our digital presence entirely. Their team delivered a platform that exceeded our expectations in both design and functionality.',
    rating: 5,
  },
  {
    name: 'Marcus Thorne', role: 'CTO, DataFlow Systems',
    avatar: 'https://images.pexels.com/photos/31307734/pexels-photo-31307734.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=100&w=100',
    quote: 'Working with Softlume was a game-changer. Their engineering team is top-notch and they truly understand scalable enterprise software.',
    rating: 5,
  },
  {
    name: 'Elena Vasquez', role: 'Product Director, InnoHealth',
    avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&h=100&fit=crop&crop=faces',
    quote: 'The mobile app they built has been instrumental in growing our user base by 300% in just six months. Exceptional work.',
    rating: 5,
  },
  {
    name: 'David Chen', role: 'Founder, LogiStar',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=faces',
    quote: "The custom software solution streamlined our entire operation. Their technical prowess and communication were exceptional.",
    rating: 5,
  },
];

const allTestimonials = [...testimonials, ...testimonials];

export default function TestimonialsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const navigate = useNavigate();

  return (
    <section id="testimonials" data-testid="testimonials-section" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-slate-950" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />

      <div ref={ref} className="relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-7xl mx-auto px-6 md:px-12 mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div>
            <span className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block">Client Voices</span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight text-white mb-4">
              Trusted by Industry Leaders
            </h2>
            <p className="text-base md:text-lg text-slate-400 max-w-xl leading-relaxed">
              Hear from the businesses we've helped transform.
            </p>
          </div>
          <Button
            data-testid="testimonials-explore-more"
            onClick={() => navigate('/testimonials')}
            className="bg-transparent border border-white/10 text-slate-300 hover:text-white hover:border-blue-500/40 hover:bg-blue-500/5 rounded-full px-6 h-10 text-sm font-medium transition-all duration-300 group self-start md:self-auto"
          >
            Read All Reviews
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="relative"
        >
          <div className="absolute left-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-r from-slate-950 to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-l from-slate-950 to-transparent z-10" />

          <div className="flex animate-marquee hover:[animation-play-state:paused]">
            {allTestimonials.map((t, index) => (
              <motion.div
                key={`${t.name}-${index}`}
                data-testid={`testimonial-card-${index}`}
                whileHover={{ scale: 1.02, y: -4 }}
                transition={{ duration: 0.3 }}
                className="flex-shrink-0 w-[380px] mx-3 p-6 rounded-2xl border border-white/5 bg-slate-900/30 backdrop-blur-sm hover:border-blue-500/20 transition-all duration-300"
              >
                <Quote className="w-8 h-8 text-blue-500/20 mb-4" />
                <p className="text-sm text-slate-300 leading-relaxed mb-6">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover border border-white/10" />
                  <div>
                    <div className="text-sm font-medium text-white">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                  <div className="ml-auto flex gap-0.5">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
